import { supabase } from '../lib/supabase';


/**
 * Diagnostic service to check subscription sync status
 * Use this to debug subscription display issues
 */
export const subscriptionDiagnosticService = {
  /**
   * Get complete subscription diagnostic data for a user
   * @param {string} userEmail - User's email address
   * @returns {Promise<object>} Diagnostic data
   */
  async getDiagnosticData(userEmail) {
    try {
      console.log('=== SUBSCRIPTION DIAGNOSTIC START ===');
      console.log('Checking user:', userEmail);

      // 1. Get user ID from auth.users
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError || !user) {
        console.error('Auth error:', authError);
        return { error: 'User not authenticated' };
      }

      console.log('User ID:', user?.id);

      // 2. Get user_subscriptions record
      const { data: subscription, error: subError } = await supabase?.from('user_subscriptions')?.select('*')?.eq('user_id', user?.id)?.maybeSingle();

      if (subError) {
        console.error('Subscription fetch error:', subError);
        return { error: 'Failed to fetch subscription', details: subError };
      }

      // If no subscription found, return diagnostic info
      if (!subscription) {
        return { 
          userId: user?.id,
          userEmail: user?.email,
          subscription: null,
          error: 'User has no subscription record',
          diagnosis: {
            issue: 'No subscription found in database',
            solution: 'User may be on trial or free plan'
          }
        };
      }

      console.log('Raw user_subscriptions record:', subscription);

      // 3. Get subscription_plans record
      const { data: plan, error: planError } = await supabase?.from('subscription_plans')?.select('*')?.eq('id', subscription?.plan_id)?.single();

      if (planError) {
        console.error('Plan fetch error:', planError);
        return { error: 'Failed to fetch plan', details: planError };
      }

      console.log('subscription_plans record:', plan);

      // 4. Get all available plans for comparison
      const { data: allPlans, error: allPlansError } = await supabase?.from('subscription_plans')?.select('*')?.eq('is_active', true);

      if (allPlansError) {
        console.error('All plans fetch error:', allPlansError);
      }

      console.log('All available plans:', allPlans);

      const diagnostic = {
        userId: user?.id,
        userEmail: user?.email,
        subscription: {
          id: subscription?.id,
          plan_id: subscription?.plan_id,
          status: subscription?.status,
          stripe_customer_id: subscription?.stripe_customer_id,
          stripe_subscription_id: subscription?.stripe_subscription_id,
          current_period_end: subscription?.current_period_end,
          billing_interval: subscription?.billing_interval,
        },
        currentPlan: {
          id: plan?.id,
          plan_type: plan?.plan_type,
          name: plan?.name,
          price_monthly: plan?.price_monthly,
        },
        allPlans: allPlans?.map(p => ({
          id: p?.id,
          plan_type: p?.plan_type,
          name: p?.name,
          price_monthly: p?.price_monthly,
        })),
        diagnosis: {
          displayedPlan: plan?.name,
          expectedPlan: 'Check Stripe Dashboard',
          issue: plan?.plan_type === 'basic' ? 'Database shows Basic, but Stripe may show Premium' : 'Plan appears correct',
          solution: plan?.plan_type === 'basic' ? 'Run manual SQL update or fix webhook' : 'No action needed',
        },
      };

      console.log('=== DIAGNOSTIC SUMMARY ===');
      console.log(JSON.stringify(diagnostic, null, 2));
      console.log('=== SUBSCRIPTION DIAGNOSTIC END ===');

      return diagnostic;
    } catch (error) {
      console.error('Diagnostic error:', error);
      return { error: 'Diagnostic failed', details: error?.message };
    }
  },

  /**
   * Force refresh subscription from Stripe (requires webhook to be working)
   * This doesn't fix the issue but helps verify webhook status
   */
  async testWebhookConnection() {
    console.log('Testing webhook connection...');
    console.log('Check Stripe Dashboard → Webhooks → Event deliveries');
    console.log('Look for recent checkout.session.completed events');
    return {
      message: 'Check Stripe Dashboard for webhook delivery status',
      steps: [
        '1. Go to Stripe Dashboard → Webhooks',
        '2. Click on your webhook endpoint',
        '3. Check "Event deliveries" tab',
        '4. Look for recent events and their status',
        '5. If failing, check STRIPE_WEBHOOK_SECRET in Supabase',
      ],
    };
  },
};

// Export for use in components
export default subscriptionDiagnosticService;